import { RouteCodes } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { InlineMessage } from '@strata/react-inline-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { setTestReferralData } from 'store/testReferral/testReferralSlice';

export const Sections = {
    PATIENT_AND_PHARMACY: 'patientAndPharmacy',
    PRODUCT: 'product',
    RESULTS: 'results',
};

export const SectionToStepMap = {
    [Sections.PATIENT_AND_PHARMACY]: 1,
    [Sections.PRODUCT]: 2,
    [Sections.RESULTS]: 3,
};

export const StepToSectionMap = {
    1: Sections.PATIENT_AND_PHARMACY,
    2: Sections.PRODUCT,
    3: Sections.RESULTS,
};

const ResultMessages = {
    ASSIGNED: {
        label: (pharmacy) => `This referral will be assigned to ${pharmacy}.`,
        intent: InlineMessage.Intent.SUCCESS,
    },
    NOT_ASSIGNED: {
        label: (pharmacy) => `This referral will not be assigned to ${pharmacy}.`,
        intent: InlineMessage.Intent.ERROR,
    },
    INFO_MKT: {
        label: () => 'This referral will update to a Live Referral.',
        intent: InlineMessage.Intent.INFO,
    },
    INFO_FMH: {
        label: () => 'This referral will be sent to Free Market Health for review and processing.',
        intent: InlineMessage.Intent.INFO,
    },
    MKT: {
        label: () => 'This referral will update to a Live Referral.',
        intent: InlineMessage.Intent.SUCCESS,
    },
    NOT_MKT: {
        label: () => 'This referral will not update to a Live Referral.',
        intent: InlineMessage.Intent.ERROR,
    },
    INFO_PHARM: {
        label: () => 'This referral will be sent to a specific Pharmacy.',
        intent: InlineMessage.Intent.INFO,
    },
};

const PharmacyDescriptors = {
    PRE_ASSIGNED: 'Pre-assigned Pharmacy',
    CONTRACTED: 'Contracted Pharmacy',
    ELIGIBLE_PHARM: 'Eligible Pharmacies',
    ASSIGNED: 'Assigned Pharmacy',
};

const sentToPharmacyStatusMessage = ({ pharmacySelected, pharmacyMatched }) => {
    if (pharmacySelected) {
        if (pharmacyMatched) {
            return ResultMessages.ASSIGNED;
        }
        return ResultMessages.NOT_ASSIGNED;
    }
    return ResultMessages.INFO_PHARM;
};

const sentToMarketplaceStatusMessage = ({ pharmacySelected }) =>
    pharmacySelected ? ResultMessages.INFO_MKT : ResultMessages.MKT;

export const routeResultsMap = {
    [RouteCodes.CHOICE]: { pharmacyDescriptor: PharmacyDescriptors.PRE_ASSIGNED },
    [RouteCodes.CONTRACT]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.CONTRACTED,
    },
    [RouteCodes.DEFAULT]: {
        statusMessage: sentToMarketplaceStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ELIGIBLE_PHARM,
    },
    [RouteCodes.DIRECT]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.FOLLOW_ON]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.MEMBER_PREFERENCE]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.ACCEPT]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.NETWORK]: {
        statusMessage: sentToMarketplaceStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ELIGIBLE_PHARM,
    },
    [RouteCodes.NEW_MEMBER]: { statusMessage: () => ResultMessages.INFO_FMH },
    [RouteCodes.NO_MARKET]: { statusMessage: () => ResultMessages.INFO_FMH },
    [RouteCodes.OON_MKT]: {
        statusMessage: sentToMarketplaceStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ELIGIBLE_PHARM,
    },
    [RouteCodes.OON_ROFR]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.QUICK_FIRE]: { statusMessage: () => ResultMessages.INFO_FMH },
    [RouteCodes.ROFR]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ASSIGNED,
    },
    [RouteCodes.UNMANAGED]: {
        statusMessage: ({ pharmacySelected }) =>
            pharmacySelected ? ResultMessages.NOT_ASSIGNED : ResultMessages.NOT_MKT,
    },
    [RouteCodes.ROUND_ROBIN]: {
        statusMessage: sentToPharmacyStatusMessage,
        pharmacyDescriptor: PharmacyDescriptors.ELIGIBLE_PHARM,
    },
};

export const optionalParameterHelperText =
    'If this information is available, it will increase the accuracy of the test.';

export const TestReferralReduxListener = () => {
    const dispatch = useDispatch();
    const { values, touched, validateForm } = useFormikContext();
    const clonedData = useMemo(
        () =>
            structuredClone({
                values,
                touched,
            }),
        [values, touched],
    );
    useEffect(() => {
        dispatch(setTestReferralData(clonedData));
    }, [dispatch, clonedData]);

    useEffect(() => {
        validateForm();
    }, [values, validateForm]);

    return null;
};

export const selectOptionSort = ({ name: a }, { name: b }) => sortAsc(a, b);

const required = validationStrings.requiredField.errorMessage;
export const validationSchema = Yup.object({
    patientAndPharmacy: Yup.object({
        includePendStart: Yup.bool().required(required),
        pharmacy: Yup.object(),
        healthPlan: Yup.object().required(required),
        patientFirstName: Yup.string().required(required),
        patientLastName: Yup.string(),
        cardholderId: Yup.string().required(required),
        memberDOB: Yup.date().typeError(required).required(required),
        benefit: Yup.object().required(required),
        prescriber: Yup.string()
            .length(10, validationStrings.exactCharacter.lambda(10))
            .required(required),
        placeOfService: Yup.object().required(required),
        eligibility: Yup.object({
            is_eligible: Yup.bool(),
            eligibilityConfirmed: Yup.bool().oneOf([true]).required(required),
            benefit_group: Yup.string(),
            member_zip: Yup.string(),
        }).required(),
    }),
    product: Yup.object({
        product: Yup.object().required(required),
        diagnosis: Yup.object(),
        foundResult: Yup.bool().oneOf([true]).required(required),
    }).required(),
});
